import { getAccountId, leitenFeature, leitenRecord, leitenRequest } from "@helium10/re-core";
import type { ILoadFile, ITableFilter } from "@helium10/re-ui-components";
import { create } from "zustand";
import {
  type IPack,
  type IClaim,
  type IProductParams,
  type IClaimsParams,
  type IUploadResponseInfo,
  type IUpdateResponse,
  type IUpdateRecordParams,
  type IUpdatePackTypeParams,
  type IClaimDetailParams,
  type IClaimDetail,
  type IClaimsConfirm,
  type IClaimDetailOuterParams,
  downTemplate,
  exportProductList,
  getProductList,
  packfeeUploadFile,
  getClaimsList,
  getFirstEntry,
  updateRecord,
  updatePackType,
  getClaimDetail,
  setClaimConfirm,
} from "../request";
import { downloadXlsxFile } from "@/utils/downloadFile";
import { DiscrepancyType, transformFilename } from "../utils";
import { nanoid } from "nanoid";
import queryString from "query-string";
import { useManageServiceInfoController } from "@helium10/re-features";

export type IFileUploadStatus = "init" | "uploading" | "success" | "failed" | "partialFailure";

export enum ActiveTab {
  Product = "product",
  Claims = "claims",
}

export enum NoteType {
  NoCase = "NoCase", // 获取详细信息,校验是否需要开案,无需开案的弹出该内容
  SameTimeConfirm = "SameTimeConfirm", // 确认之后展示,同时确认其他多出来的market
  MoreThan20 = "MoreThan20", // 确认之后展示,超出重新测量限制
}
export interface IPackStore {
  activeTab: ActiveTab;
  firstResponse: {
    isFirst: boolean;
  };
  sellerMarkets: { seller: string; market: string }[]; // selected MarketPlace & profile
  productFilter: ITableFilter & { missingProduct?: boolean };
  productResponse: {
    // all product 数据
    data: IPack[];
    totalCount: number;
  } | null;
  productExportResponse: any; // all product - export 功能
  uploadClaimStep: number; // upload弹窗步骤
  uploadClaimOpen: boolean; // upload弹窗是否展示
  historyOpen: boolean; // 历史下载弹窗
  paritialFailedOpen: boolean; // 部分失败弹窗
  downTemplateResponse: any; // 下载模板
  uploadFileResponse: IUploadResponseInfo | null; // 上传响应数据
  fileData: {
    // 文件记录
    files: { metadata: File; blob: Blob }[];
    attachId?: number;
    progress: number;
    status: IFileUploadStatus;
    result: {
      success: number;
      failed: number;
    };
    errorMsg: string;
  };
  claimsFilter: ITableFilter;
  claimsResponse: {
    // claims list 数据
    data: IClaim[];
    totalCount: number;
  } | null;
  updateClaimRecord: IUpdateResponse | null;
  updateClaimPackType: IUpdateResponse | null;
  checkClaimResponse: IClaimDetail | null;
  confirmClaimResponse: IClaimsConfirm | null;
  confirmClaimOpen: boolean;
  noteOpen: boolean;
  noteType: NoteType | null;
}

export const createFilter = (
  type?: "product" | "claims",
): ITableFilter & { missingProduct?: boolean } => ({
  search: "",
  page: 1,
  count: 10,
  sortDirection: "DESC",
  sortField:
    type === "claims"
      ? (queryString.parseUrl(window.location.href).query.sortField as string) ?? "operational" // Claims 默认按照 operational 排序
      : "",
  missingProduct:
    type === "product"
      ? !!queryString.parseUrl(window.location.href).query.missingProduct
      : undefined,
});

export const [usePackfee, PackfeeProvider] = leitenFeature(() => {
  const useStore = create<IPackStore>(() => ({
    activeTab: sessionStorage.getItem("PACKFEETAB")
      ? JSON.parse(sessionStorage.getItem("PACKFEETAB")!)?.[
          queryString.parseUrl(window.location.href)?.query?.accountId as string
        ] || ActiveTab.Product
      : ActiveTab.Product,
    firstResponse: {
      isFirst: false, // 是否第一次进入该页面,用来控制遮罩
    },
    sellerMarkets: [], // 选择的account,请求主列表使用
    productFilter: createFilter("product"),
    productResponse: null, // all product 列表请求响应
    productExportResponse: null, // all product 列表导出数据 --- 导出功能不确定
    uploadClaimStep: 1, // 上传claim当前所在步骤
    uploadClaimOpen: false, // 上传claim弹窗打开/关闭
    historyOpen: false, // 历史下载弹窗
    paritialFailedOpen: false, // 部分上传失败弹窗显隐
    downTemplateResponse: null, // 下载上传模板响应
    uploadFileResponse: null, // 上传文件响应
    fileData: {
      // 上传文件的信息
      files: [],
      attachId: undefined, // 记录上传文件后返回的id,这个id用于再次下载
      progress: 0,
      status: "init", // 上传文件的状态,控制弹窗里面上传按钮的状态,同时控制页面右上角上传按钮的状态
      result: {
        success: 0,
        failed: 0,
      },
      errorMsg: "",
    },
    claimsFilter: createFilter("claims"),
    claimsResponse: null, // Pending Claims 列表请求响应
    updateClaimRecord: null, // 更新尺寸返回值
    updateClaimPackType: null, // 更新包装类型返回值
    checkClaimResponse: null, // 获取到的Claim详情
    confirmClaimResponse: null, // 确认Chaim返回值
    confirmClaimOpen: false, // 确认弹框
    noteOpen: false, // 提示弹框
    noteType: null, // 通知的类型
  }));

  const useFirstEntry = leitenRequest(useStore, "firstResponse", async () => {
    const accountId = getAccountId();
    const result = await getFirstEntry({ accountId });
    return {
      isFirst: result.data,
    };
  });
  const firstEntryControl = leitenRecord(useStore, "firstResponse");

  const productFilterControl = leitenRecord(useStore, "productFilter");

  // 获取All Product列表数据请求
  const useProductRequest = leitenRequest(
    useStore,
    "productResponse",
    async (
      params: IProductParams,
      extraArgument?: { signal?: AbortSignal },
    ): Promise<{
      data: IPack[];
      totalCount: number;
    }> => {
      const result = await getProductList(params, extraArgument);
      return result.data;
    },
    {
      initialStatus: "loading",
    },
  );

  // 导出All Product列表数据请求 - 主列表右上角导出 - 该功能还需讨论
  const useExportProductRequest = leitenRequest(
    useStore,
    "productExportResponse",
    async (params: IProductParams) => {
      const accountId = getAccountId();
      const result = await exportProductList({ ...params, accountId });
      await downloadXlsxFile(
        result,
        transformFilename(`Product Weight and Dimensions-${Date.now()}.xslx`),
      );
      return result;
    },
    {
      initialStatus: "init",
    },
  );

  // 下载上传模板请求
  const useDownTemplateRequest = leitenRequest(
    useStore,
    "downTemplateResponse",
    async (params: object) => {
      const accountId = getAccountId();
      const result = await downTemplate({ ...params, accountId });
      await downloadXlsxFile(result, transformFilename(`Product Dimensions and Weight`));
      return result;
    },
    {
      initialStatus: "init",
    },
  );
  const fileDataController = leitenRecord(useStore, "fileData");
  // 上传文件请求
  const useUploadRequest = leitenRequest(
    useStore,
    "uploadFileResponse",
    async ({ lFiles }: { lFiles: ILoadFile[] }) => {
      // 上传中的状态需要改变,按钮要显示loading状态
      fileDataController.patch({
        progress: 0,
        status: "uploading",
      });
      const accountId = getAccountId();
      const result = await packfeeUploadFile({
        accountId,
        file: {
          fileBlob: lFiles[0].blob,
          fileName: lFiles[0].metadata.name,
        },
      });
      // 只要接口成功,更新顶部菜单的reminder board
      if (result.code === 200) {
        useManageServiceInfoController.action({ forceRefresh: true });
      }
      if (result.code !== 200) {
        // 接口通了,但是后端未解析成功,此时要给出提示信息(此时只是给出提示信息,不展示部分失败弹窗)
        fileDataController.patch({
          status: "partialFailure",
          errorMsg: result.message,
        });
      }
      // ----------------  临时测试代码 ----------------
      // !result.data.failedDetails.length
      //   ? (result.data.failedDetails = result.data.succeedDetails)
      //   : "";
      // !result.data.succeedDetails.length
      //   ? (result.data.succeedDetails = result.data.failedDetails)
      //   : "";
      // result.data.failedDetails = [];
      // ----------------  临时测试代码 ----------------
      // 失败的数据没有id,但是展示列表的时候需要id,前端自己生成一个id
      result.data.failedDetails.forEach((item) => (item.id = nanoid()));
      return result.data;
    },
    {
      fulfilled: ({ result }) => {
        // 只有 result.failedDetails.length 中有内容的时候才要弹出部分失败的弹窗进行展示
        if (result?.failedDetails?.length) {
          fileDataController.patch({
            status: "partialFailure",
            result: { success: result.succeedDetails.length, failed: result.failedDetails.length },
            attachId: result.attachId,
          });
          // 部分失败弹窗
          if (useStore.getState().uploadClaimOpen) {
            useStore.setState({ paritialFailedOpen: true, uploadClaimOpen: false });
          }
          return;
        }
        // 如果不是部分失败,那么一定是成功的,此时弹出 Toast 提示
        fileDataController.patch({
          status: "success",
          result: { success: result.succeedDetails.length, failed: 0 },
          attachId: result.attachId,
        });
      },
      rejected: (data) => {
        // 因技术原因和网络原因导致失败的弹出提示
        fileDataController.patch({
          status: "failed",
        });
      },

      initialStatus: "loading",
    },
  );

  // 获取Pending Claims列表数据请求
  const claimsFilterControl = leitenRecord(useStore, "claimsFilter");
  const claimsControl = leitenRecord(useStore, "claimsResponse");
  const useClaimsRequest = leitenRequest(
    useStore,
    "claimsResponse",
    async (
      params: IClaimsParams,
      extraArgument?: { signal?: AbortSignal },
    ): Promise<{
      data: IClaim[];
      totalCount: number;
    }> => {
      const result = await getClaimsList(params, extraArgument);
      return result?.data;
    },
    {
      initialStatus: "loading",
    },
  );

  const updateClaimsRecordRequest = leitenRequest(
    useStore,
    "updateClaimRecord",
    async (
      _params: IUpdateRecordParams,
      extraArgument?: { signal?: AbortSignal },
    ): Promise<IUpdateResponse | null> => {
      const params = { ..._params };
      Reflect.deleteProperty(params, "updateTipHandler");
      Reflect.deleteProperty(params, "errorTipHandler");
      Reflect.deleteProperty(params, "id");
      const result = await updateRecord(params, extraArgument);
      if (result.code !== 200) {
        _params.errorTipHandler?.();
        return null;
      }
      return result?.data;
    },
    {
      initialStatus: "init",
      fulfilled: ({ payload, result }) => {
        const claimsResponse = useStore.getState().claimsResponse;
        if (result) {
          claimsControl.patch({
            data:
              claimsResponse?.data.map((item) =>
                item.id === payload.id // 唯一标识
                  ? {
                      ...item,
                      actualDimensionsLongSide: Number(payload.longestSide),
                      actualDimensionsMiddleSide: Number(payload.medianSide),
                      actualDimensionsShortSide: Number(payload.shortestSide),
                      actualWeight: Number(payload.weight),
                      updateRecordLoading: false,
                    }
                  : item,
              ) || [],
          });

          const exceedSkuList = result.skus.filter((sku) => payload.sku !== sku);
          if (exceedSkuList.length) {
            payload.updateTipHandler?.(exceedSkuList);
          } else {
            updateClaimsRecordRequest.clear();
          }
        } else {
          claimsControl.patch({
            data:
              claimsResponse?.data.map((item) =>
                item.id === payload.id // 唯一标识
                  ? {
                      ...item,
                      updateRecordLoading: false,
                    }
                  : item,
              ) || [],
          });
        }
      },
    },
  );

  const updateClaimsPackTypeRequest = leitenRequest(
    useStore,
    "updateClaimPackType",
    async (
      _params: IUpdatePackTypeParams,
      extraArgument?: { signal?: AbortSignal },
    ): Promise<IUpdateResponse | null> => {
      const params = { ..._params };
      Reflect.deleteProperty(params, "updateTipHandler");
      Reflect.deleteProperty(params, "errorTipHandler");
      Reflect.deleteProperty(params, "id");
      const result = await updatePackType(params, extraArgument);
      if (result.code !== 200) {
        _params.errorTipHandler?.();
        return null;
      }
      return result?.data;
    },
    {
      fulfilled: ({ payload, result }) => {
        const claimsResponse = useStore.getState().claimsResponse;
        if (result) {
          claimsControl.patch({
            data:
              claimsResponse?.data.map((item) =>
                item.id === payload.id // 唯一标识
                  ? {
                      ...item,
                      packingType: payload.packingType,
                      updatePackTypeLoading: false,
                    }
                  : item,
              ) || [],
          });

          const exceedSkuList = result.skus.filter((sku) => payload.sku !== sku);
          if (exceedSkuList.length) {
            payload.updateTipHandler?.(exceedSkuList);
          } else {
            updateClaimsPackTypeRequest.clear();
          }
        } else {
          claimsControl.patch({
            data:
              claimsResponse?.data.map((item) =>
                item.id === payload.id // 唯一标识
                  ? {
                      ...item,
                      updatePackTypeLoading: false,
                    }
                  : item,
              ) || [],
          });
        }
      },
    },
  );

  const checkClaimRequest = leitenRequest(
    useStore,
    "checkClaimResponse",
    async (
      _params: IClaimDetailOuterParams,
      extraArgument?: { signal?: AbortSignal },
    ): Promise<IClaimDetail | null> => {
      const params: IClaimDetailParams = {
        accountId: _params.accountId,
        sellerId: _params.sellerId,
        countryCode: _params.countryCode,
        asin: _params.asin,
        fnsku: _params.fnsku,
        sku: _params.sku,
        businessId: _params.businessId,
      };
      const result = await getClaimDetail(params, extraArgument);
      if (result.code !== 200) {
        _params.failedTip?.();
        return null;
      }
      return {
        ...result.data,
        amazonDimensionsLongSide: _params.amazonDimensionsLongSide,
        amazonDimensionsMiddleSide: _params.amazonDimensionsMiddleSide,
        amazonDimensionsShortSide: _params.amazonDimensionsShortSide,
        amazonDimensionUnit: _params.amazonDimensionUnit,
        amazonWeight: _params.amazonWeight,
        amazonWeightUnit: _params.amazonWeightUnit,
        id: _params.id!,
      };
    },
    {
      initialStatus: "init",
      fulfilled: ({ payload, result }) => {
        if (result) {
          switch (result.discrepancyType) {
            case DiscrepancyType.OVER_CHARGED:
              useStore.setState({ confirmClaimOpen: true }); // 打开确认弹框
              break;
            case DiscrepancyType.UNDER_CHARGED:
            case DiscrepancyType.CORRECTLY_CHARGED:
              useStore.setState({ noteOpen: true, noteType: NoteType.NoCase }); // 弹出不重开case提示
              break;
            case DiscrepancyType.INCALCULABLE:
              payload.checkTipHandler?.(); // 无法预估提示
              break;
          }
        }
      },
    },
  );

  const submitConfirmRequest = leitenRequest(
    useStore,
    "confirmClaimResponse",
    async (
      _params: IClaimDetailOuterParams,
      extraArgument?: { signal?: AbortSignal },
    ): Promise<IClaimsConfirm | null> => {
      const params = { ..._params };
      Reflect.deleteProperty(params, "checkTipHandler");
      Reflect.deleteProperty(params, "failedTip");
      Reflect.deleteProperty(params, "id");
      const result = await setClaimConfirm(params, extraArgument);
      if (result.code !== 200) {
        _params.failedTip?.();
        return null;
      }
      return result.data;
    },
    {
      fulfilled: ({ payload, result }) => {
        if (result) {
          // 确认之后展示,同时确认其他多出来的market
          if (result.asinCountryList.filter((country) => country !== payload.countryCode).length) {
            useStore.setState({
              confirmClaimOpen: false,
              noteOpen: true,
              noteType: NoteType.SameTimeConfirm,
            });
            return;
          }
          // 确认之后展示,超出重新测量限制
          if (result.moreThan20) {
            useStore.setState({
              confirmClaimOpen: false,
              noteOpen: true,
              noteType: NoteType.MoreThan20,
            });
            return;
          }
          // 正常情况下应该变成 Confirmed
          const claimsResponse = useStore.getState().claimsResponse;
          useStore.setState({
            confirmClaimOpen: false,
            ...(claimsResponse
              ? {
                  claimsResponse: {
                    ...claimsResponse,
                    data:
                      claimsResponse?.data.map((item) =>
                        item.id === payload.id // 唯一标识
                          ? {
                              ...item,
                              operational: false,
                            }
                          : item,
                      ) || [],
                  },
                }
              : {}),
            checkClaimResponse: null,
            confirmClaimResponse: null,
          });
        }
      },
    },
  );

  return {
    useStore,
    firstEntryControl,
    useFirstEntry,
    productFilterControl,
    useProductRequest,
    useExportProductRequest,
    useDownTemplateRequest,
    fileDataController,
    useUploadRequest,
    claimsFilterControl,
    claimsControl,
    useClaimsRequest,
    updateClaimsRecordRequest,
    updateClaimsPackTypeRequest,
    checkClaimRequest,
    submitConfirmRequest,
  };
});
